"use client";

import Link from "next/link";
import FoodCustomersPanel from "./components/FoodCustomersPanel";
import HomepagePassengerRadar from "./components/HomepagePassengerRadar";
import HomepageDriverPanel from "./components/HomepageDriverPanel";
import HomepageOfferRidePanel from "./components/HomepageOfferRidePanel";
import HomepageDriverTileAvailability from "./components/HomepageDriverTileAvailability";
import {
  HomepageDriverSessionContext,
  type DriverAvailabilitySession,
} from "@/lib/homepageDriverSession";
import RideBookingConfirmation from "./components/RideBookingConfirmation";
import PassengerRideRequestPanel from "./components/PassengerRideRequestPanel";
import HomepageGoodsTransportPanel from "./components/HomepageGoodsTransportPanel";
import RideRoutePreview from "@/app/components/RideRoutePreview";
import NearbyRidesPanel from "./components/NearbyRidesPanel";
import CompactRideList from "./components/CompactRideList";
import RideModeMenu from "./components/RideModeMenu";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell, User, ShieldCheck, LogOut, CarFront, Users, Utensils,
  ChefHat, Menu, X, MapPin, Loader2, Home, Store, Tag,
  ListOrdered, HeartHandshake, LifeBuoy, PackageSearch,
  PackagePlus, Activity, ChevronRight, Search, Truck,
} from "lucide-react";

type LoggedInUser = { id: number; name: string; mobile: string; foodSellerAvailable?: boolean };
type NotificationItem = { id: number; isRead: boolean };

const primaryServices = [
  { title: "Ride Now", description: "Request a ride now", href: "/find-ride", icon: CarFront },
  { title: "Goods Transport", description: "Book a whole goods vehicle", href: "/", icon: Truck },
  { title: "DRIVER", description: "Register or manage your vehicle", href: "/driver", icon: CarFront },
  { title: "Find Food", description: "Explore food nearby", href: "/buy-food", icon: Utensils },
  { title: "Offer Food", description: "Share your cooking", href: "/offer-food", icon: ChefHat },
];

const menuSections = [
  { title: "Rides", links: [
    { label: "Ride Now", href: "/find-ride", icon: CarFront },
    { label: "DRIVER", href: "/driver", icon: CarFront },
    { label: "Offer a Ride", href: "/offer-ride", icon: CarFront },
  ] },
  { title: "Food", links: [
    { label: "Find Food", href: "/buy-food", icon: Utensils },
    { label: "Find Food Customers", href: "/food/find-customers", icon: Users },
    { label: "Offer Food", href: "/offer-food", icon: ChefHat },
  ] },
  { title: "Marketplace", links: [
    { label: "Buy Products", href: "/marketplace", icon: Store },
    { label: "Sell Product", href: "/sell-product", icon: Tag },
    { label: "My Products", href: "/my-products", icon: ListOrdered },
  ] },
  { title: "Community", links: [
    { label: "House Rent", href: "/house-rent", icon: Home },
    { label: "Need Help", href: "/get-help", icon: LifeBuoy },
    { label: "Give Help", href: "/help-requests", icon: HeartHandshake },
  ] },
  { title: "Parcels", links: [
    { label: "Send Parcel", href: "/send-something", icon: PackageSearch },
    { label: "Carry Parcel", href: "/carry-something", icon: PackagePlus },
  ] },
];

export default function HomePage() {
  const [user, setUser] = useState<LoggedInUser | null>(null);
  const [approvedFoodSellerName, setApprovedFoodSellerName] = useState("");
  const [foodSellerAvailable, setFoodSellerAvailable] = useState(false);
  const [updatingFoodSellerAvailability, setUpdatingFoodSellerAvailability] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [locationName, setLocationName] = useState("India");
  const [gettingLocation, setGettingLocation] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchEditing, setSearchEditing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{
    lat: string | null;
    lon: string | null;
    display_name: string;
    type: string | null;
    villageCode?: string;
  }>>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [locationDetails, setLocationDetails] = useState("");

  // Shared homepage map controls for Find Ride.
  const [mobileRideMenuOpen, setMobileRideMenuOpen] = useState(false);
  const [mobileFoodMenuOpen, setMobileFoodMenuOpen] = useState(false);
  const [findFoodOpen, setFindFoodOpen] = useState(false);
  const [ridePanelOpen, setRidePanelOpen] = useState(false);
  const [hasPassengerTrip, setHasPassengerTrip] = useState(false);
  const [hasActivePassengerTrip, setHasActivePassengerTrip] = useState(false);
  const [hasDriverTrip, setHasDriverTrip] = useState(false);

  // Restore DRIVER active booking after Homepage refresh.
  useEffect(() => {
    if (!user) {
      setHasDriverTrip(false);
      return;
    }

    let active = true;

    async function checkDriverTrip() {
      try {
        const response = await fetch(
          "/api/ride-requests?mode=ride-now-accepted",
          { cache: "no-store" }
        );
        if (!response.ok) return;

        const data: unknown = await response.json();
        if (!active) return;

        setHasDriverTrip(
          Array.isArray(data) &&
          data.some((trip: { status?: string }) =>
            trip.status === "Accepted" || trip.status === "Started"
          )
        );
      } catch {
        // Keep previous state during temporary network issues.
      }
    }

    void checkDriverTrip();
    const timer = window.setInterval(() => void checkDriverTrip(), 5000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [user?.id]);
  const [rideStep, setRideStep] = useState<1 | 2 | 3>(1);
  const [existingRideDate, setExistingRideDate] = useState("");
  const [existingRideResultsOpen, setExistingRideResultsOpen] = useState(false);
  const [rideMode, setRideMode] = useState<"ride_now" | "destination" | "explore" | "scheduled">("ride_now");
  const [rideMapPickMode, setRideMapPickMode] = useState<"pickup" | "destination">("destination");
  const [foodCustomersOpen, setFoodCustomersOpen] = useState(false);
  const [passengersOpen, setPassengersOpen] = useState(false);
  const [goodsTransportOpen, setGoodsTransportOpen] = useState(false);
  const [offerRideOpen, setOfferRideOpen] = useState(false);
  const [homepageOfferStep, setHomepageOfferStep] = useState<1 | 2 | 3>(1);
  const [offerDestination, setOfferDestination] = useState<{
    latitude: number;
    longitude: number;
    label: string;
  } | null>(null);
  const [driverOpen, setDriverOpen] = useState(false);
  const [driverOnlineCategory, setDriverOnlineCategory] = useState<"Passenger" | "Goods" | null>(null);
  // Persist across DRIVER tile/panel remounts on this homepage.
  const driverSessionRef = useRef<DriverAvailabilitySession | null>(null);
  const driverSessionOwnerRef = useRef<number | null>(null);
  const driverSessionContextValue = useMemo(
    () => ({ userId: user?.id ?? null, sessionRef: driverSessionRef }),
    [user?.id]
  );

  if (driverSessionOwnerRef.current !== user?.id) {
    driverSessionRef.current = null;
    driverSessionOwnerRef.current = user?.id ?? null;
  }
  const [sharedMap, setSharedMap] = useState<import("leaflet").Map | null>(null);
  const [ridePickup, setRidePickup] = useState<{
    latitude: number;
    longitude: number;
    label: string;
  } | null>(null);
  const [rideDestination, setRideDestination] = useState<{
    latitude: number;
    longitude: number;
    label: string;
  } | null>(null);
  const [ridePickupQuery, setRidePickupQuery] = useState("");
  const [ridePickupSuggestions, setRidePickupSuggestions] = useState<Array<{
    name: string;
    latitude: number;
    longitude: number;
  }>>([]);
  const [ridePickupLoading, setRidePickupLoading] = useState(false);
  const [ridePickupError, setRidePickupError] = useState("");
  const [rideDestinationQuery, setRideDestinationQuery] = useState("");
  const [rideDestinationSuggestions, setRideDestinationSuggestions] = useState<Array<{
    name: string;
    latitude: number;
    longitude: number;
  }>>([]);
  const [rideDestinationLoading, setRideDestinationLoading] = useState(false);
  const [rideDestinationError, setRideDestinationError] = useState("");

  type CompactRide = {
    id: number;
    name: string;
    photo: string | null;
    from: string;
    to: string;
    seats: number;
    dateTime: string;
    latitude: number | null;
    longitude: number | null;
  };

  const [compactRideRows, setCompactRideRows] = useState<CompactRide[]>([]);
  const [compactRideLoading, setCompactRideLoading] = useState(false);
  const [compactRideError, setCompactRideError] = useState("");
  const [compactRideNeedsLogin, setCompactRideNeedsLogin] = useState(false);
  const [selectedCompactRideId, setSelectedCompactRideId] =
    useState<number | null>(null);
  const selectedCompactRide =
    compactRideRows.find((ride) => ride.id === selectedCompactRideId) ?? null;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<import("leaflet").Map | null>(null);
  const locationMarkerRef = useRef<import("leaflet").Marker | null>(null);
  const locationSelectionRef = useRef(0);
  const detectLocationRef = useRef<() => void>(() => {});
  const offerMapDragEnabledRef = useRef(false);
  const rideMapClickRef =
    useRef<(latitude: number, longitude: number) => void>(() => {});
  const rideDestinationMarkerRef =
    useRef<import("leaflet").CircleMarker | null>(null);
  const rideSearchSequenceRef = useRef(0);
  const compactRideMarkersRef =
    useRef<import("leaflet").LayerGroup | null>(null);


  useEffect(() => {
    let disposed = false;
    let map: import("leaflet").Map | null = null;
    let cancelPlacesRequest: (() => void) | null = null;

    async function initializeMap() {
      const L = await import("leaflet");
      if (disposed || !mapRef.current || leafletMapRef.current) return;
      map = L.map(mapRef.current, { zoomControl: false, attributionControl: true })
        .fitBounds([[6.0, 68.0], [38.0, 98.0]], { padding: [20, 20] });
      leafletMapRef.current = map;
      setSharedMap(map);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);
      L.control.zoom({ position: "bottomright" }).addTo(map);

      // Reuse this map instead of opening a second ride map.
      map.on("click", ({ latlng }) => {
        rideMapClickRef.current(latlng.lat, latlng.lng);
      });

      // Use the same selection handler when the map is dragged.
      map.on("dragend", () => {
        if (!offerMapDragEnabledRef.current || !map) return;
        const center = map.getCenter();
        rideMapClickRef.current(center.lat, center.lng);
      });

      // Important Places are loaded only for the visible map area.
      const placesLayer = L.layerGroup().addTo(map);
      let placesRequest: AbortController | null = null;
      let placesRequestNumber = 0;

      cancelPlacesRequest = () => {
        placesRequest?.abort();
      };

      async function loadImportantPlaces() {
        if (!map || disposed) return;

        placesRequest?.abort();

        // Keep the map clean at country/state/city overview zoom levels.
        if (map.getZoom() < 14) {
          ++placesRequestNumber;
          placesLayer.clearLayers();
          return;
        }

        const controller = new AbortController();
        placesRequest = controller;
        const requestNumber = ++placesRequestNumber;

        const bounds = map.getBounds();
        const bbox = [
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth(),
        ].join(",");

        try {
          const response = await fetch(
            `/api/places/map?bbox=${encodeURIComponent(bbox)}&zoom=${map.getZoom()}`,
            { signal: controller.signal }
          );

          if (!response.ok) throw new Error("Unable to load Important Places");

          const result = await response.json() as
            | {
                type: "places";
                data: Array<{
                  id: number;
                  name: string;
                  category: string;
                  latitude: number;
                  longitude: number;
                }>;
              }
            | {
                type: "clusters";
                data: Array<{
                  latitude: number;
                  longitude: number;
                  count: number;
                }>;
              };

          if (
            disposed ||
            controller.signal.aborted ||
            requestNumber !== placesRequestNumber
          ) return;

          placesLayer.clearLayers();

          if (result.type === "places") {
            for (const place of result.data) {
              const marker = L.circleMarker(
                [place.latitude, place.longitude],
                {
                  radius: 7,
                  color: "#166534",
                  fillColor: "#22c55e",
                  fillOpacity: 0.95,
                  weight: 2,
                }
              );

              // Leaflet treats tooltip content as HTML; use textContent
              // to safely display names imported from OpenStreetMap.
              const tooltip = document.createElement("span");
              tooltip.textContent = place.name;
              marker.bindTooltip(tooltip, { direction: "top" });

              const popup = document.createElement("div");
              const name = document.createElement("strong");
              name.textContent = place.name;
              const category = document.createElement("div");
              category.textContent = place.category;
              popup.append(name, category);
              marker.bindPopup(popup);

              marker.addTo(placesLayer);
            }
          } else {
            for (const cluster of result.data) {
              const count = cluster.count;
              const marker = L.circleMarker(
                [cluster.latitude, cluster.longitude],
                {
                  radius: count > 1 ? Math.min(22, 9 + Math.log10(count) * 4) : 5,
                  color: "#166534",
                  fillColor: count > 1 ? "#22c55e" : "#4ade80",
                  fillOpacity: 0.85,
                  weight: 2,
                }
              );

              marker.bindTooltip(
                count > 1
                  ? `${count.toLocaleString()} Important Places`
                  : "Important Place"
              );

              marker.addTo(placesLayer);
            }
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error("Important Places could not load:", error);
          }
        }
      }

      map.on("moveend", loadImportantPlaces);
      void loadImportantPlaces();

      // Detect location on startup, respecting the browser's permission state.
      requestAnimationFrame(() => {
        if (disposed) return;
        map?.invalidateSize();

        void (async () => {
          if (!navigator.geolocation) return;

          if (navigator.permissions?.query) {
            try {
              const permission = await navigator.permissions.query({
                name: "geolocation",
              });
              if (disposed || permission.state === "denied") return;
            } catch {
              // Some browsers do not support querying geolocation permission.
            }
          }

          if (!disposed && locationSelectionRef.current === 0) {
            detectLocationRef.current();
          }
        })();
      });
    }
    void initializeMap().catch((error) => console.error("Map could not load:", error));
    return () => {
      disposed = true;
      cancelPlacesRequest?.();
      map?.remove();
      leafletMapRef.current = null;
      locationMarkerRef.current = null;
      rideDestinationMarkerRef.current = null;
      compactRideMarkersRef.current = null;
    };
  }, []);

  useEffect(() => {
    function closeOnOutsideClick(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const checkAdminAccess = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/summary", { cache: "no-store" });
      setIsAdmin(response.ok);
    } catch (error) {
      console.error("Unable to check admin access:", error);
      setIsAdmin(false);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await fetch("/api/notifications", { cache: "no-store" });
      if (!response.ok) { setUnreadCount(0); return; }
      const data: unknown = await response.json();
      setUnreadCount(Array.isArray(data) ? data.filter((n: NotificationItem) => !n.isRead).length : 0);
    } catch (error) {
      console.error("Unable to load notifications:", error);
      setUnreadCount(0);
    }
  }, []);

  const loadCurrentUser = useCallback(async () => {
    try {
      const response = await fetch("/api/me", { cache: "no-store" });
      if (response.ok) {
        const loggedInUser = (await response.json()) as LoggedInUser;
        setUser(loggedInUser);
        setApprovedFoodSellerName("");
        setFoodSellerAvailable(false);

        try {
          const sellerResponse = await fetch(
            "/api/food-seller-registration",
            { cache: "no-store" }
          );

          if (sellerResponse.ok) {
            const sellerData = await sellerResponse.json();
            const registration = sellerData.registration;

            if (registration?.status === "Approved") {
              setApprovedFoodSellerName(
                String(registration.sellerName || "").trim()
              );
              setFoodSellerAvailable(
                loggedInUser.foodSellerAvailable === true
              );
            }
          }
        } catch (error) {
          console.error("Unable to load Homepage Food Seller:", error);
        }

        await Promise.all([loadNotifications(), checkAdminAccess()]);
        return;
      }
      const savedUser = localStorage.getItem("apnasaathiUser");
      setUser(savedUser ? JSON.parse(savedUser) : null);
      setUnreadCount(0);
      setIsAdmin(false);
    } catch (error) {
      console.error("Unable to verify session:", error);
      try {
        const savedUser = localStorage.getItem("apnasaathiUser");
        setUser(savedUser ? JSON.parse(savedUser) : null);
      } catch { setUser(null); }
      setUnreadCount(0);
      setIsAdmin(false);
    } finally {
      setLoadingUser(false);
    }
  }, [loadNotifications, checkAdminAccess]);

  useEffect(() => {
    const controller = new AbortController();

    queueMicrotask(() => {
      if (!controller.signal.aborted) {
        void loadCurrentUser();
      }
    });

    return () => controller.abort();
  }, [loadCurrentUser]);

  async function showLocation(latitude: number, longitude: number, label: string, zoom = 16) {
    // Do not allow location selection to modify an active Ride Now booking.
    // DRIVER live GPS tracking is handled separately.
    if (rideMode === "ride_now" && hasActivePassengerTrip) return;

    ++locationSelectionRef.current;
    setSearchEditing(false);
    setLocationName(label);
    setSearchQuery("");
    setSuggestionsOpen(false);
    setSuggestions([]);
    setLocationDetails(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
    if (ridePanelOpen && rideMode === "ride_now" && rideStep === 2) {
      ++rideSearchSequenceRef.current;
      setRideDestination({ latitude, longitude, label });
      setRideDestinationQuery(label);
      setRideDestinationSuggestions([]);
      setRideDestinationError("");
      setRideDestinationLoading(false);
    } else {
      if (offerRideOpen && homepageOfferStep === 2) {
        setOfferDestination({ latitude, longitude, label });
      } else {
        setRidePickup({ latitude, longitude, label });
        if (ridePanelOpen && rideMode === "destination" && rideStep === 1) {
          setRidePickupQuery(label);
          setRidePickupSuggestions([]);
          setRidePickupError("");
        }
      }
    }
    const L = await import("leaflet");
    const map = leafletMapRef.current;
    if (!map) return;
    const point: [number, number] = [latitude, longitude];
    if (locationMarkerRef.current) locationMarkerRef.current.setLatLng(point);
    else {
      const walkingIcon = L.divIcon({
        className: "apnasaathi-walking-location",
        html: `
          <div class="relative flex h-12 w-12 items-center justify-center">
            <span class="absolute h-10 w-10 animate-ping rounded-full bg-blue-400/30"></span>
            <span class="absolute h-10 w-10 rounded-full border-2 border-blue-400/50 bg-blue-100/50"></span>
            <span class="relative z-10 text-2xl drop-shadow">🚶</span>
          </div>
        `,
        iconSize: [48, 48],
        iconAnchor: [24, 24],
      });
      locationMarkerRef.current = L.marker(point, {
        icon: walkingIcon,
        keyboard: false,
      }).addTo(map).bindPopup("Selected location");
    }
    map.flyTo(point, zoom);
  }

  useEffect(() => {
    const query = searchQuery.trim();

    if (query.length < 3 || !suggestionsOpen) {
      return;
    }

    const controller = new AbortController();

    const timer = setTimeout(async () => {
      setSuggestionsLoading(true);

      try {
        const response = await fetch(
          `/api/location-search?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );

        if (!response.ok) throw new Error("Suggestions unavailable");

        const results = await response.json() as Array<{
          id: number | string;
          name: string;
          latitude: number;
          longitude: number;
          type: string | null;
          villageCode?: string;
        }>;

        if (!controller.signal.aborted) {
          setSuggestions(results.slice(0, 8).map((place) => ({
            lat: place.latitude === null ? null : String(place.latitude),
            lon: place.longitude === null ? null : String(place.longitude),
            display_name: place.name,
            type: place.type,
            villageCode: place.villageCode,
          })));
        }
      } catch {
        if (!controller.signal.aborted) setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setSuggestionsLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery, suggestionsOpen]);

  async function searchPlace(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = searchQuery.trim();
    if (query.length < 3 || searching) return;
    setSearching(true);
    setLocationError("");
    try {
      const response = await fetch(`/api/geocode?q=${encodeURIComponent(query)}`, { cache: "no-store" });
      if (!response.ok) {
        const failure = await response.json().catch(() => ({})) as { message?: string };
        throw new Error(failure.message || "Place search is temporarily unavailable.");
      }
      const places = (await response.json()) as Array<{ lat: string; lon: string; display_name: string }>;
      if (!places.length) { setLocationError("No matching place found. Try a more specific address."); return; }
      const place = places[0];
      await showLocation(Number(place.lat), Number(place.lon), place.display_name);
    } catch (error) {
      setLocationError(error instanceof Error ? error.message : "Could not search right now. Please try again.");
    } finally {
      setSearching(false);
    }
  }

  function detectLocation() {
    if (!navigator.geolocation) { setLocationError("Location is not supported by this browser."); return; }
    const selectionAtStart = locationSelectionRef.current;
    setGettingLocation(true);
    setLocationError("");
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        if (locationSelectionRef.current !== selectionAtStart) {
          setGettingLocation(false);
          return;
        }
        const { latitude, longitude, accuracy } = coords;
        const coordinateLabel = `Your location (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`;
        let gpsSelection = locationSelectionRef.current;
        try {
          await showLocation(latitude, longitude, coordinateLabel, 14);
          gpsSelection = locationSelectionRef.current;
          setLocationDetails(`GPS accuracy: approximately ${Math.round(accuracy)} m · ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          const response = await fetch(`/api/geocode?lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`, { cache: "no-store" });
          if (response.ok && locationSelectionRef.current === gpsSelection) {
            const result = (await response.json()) as { display_name?: string };
            if (result.display_name && locationSelectionRef.current === gpsSelection) {
              setLocationName(result.display_name);
            }
          }
        } catch {
          // GPS coordinates and map marker remain valid even when
          // reverse geocoding fails because the network is offline.
          if (locationSelectionRef.current === gpsSelection) {
            setLocationDetails(
              `GPS location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)} · ` +
              `Accuracy approximately ${Math.round(accuracy)} m · Address unavailable offline`
            );
          }
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        if (locationSelectionRef.current === selectionAtStart) {
          setLocationError(error.code === 1 ? "Allow location access in your browser to use GPS." : "Could not detect your location. Try searching for a place.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }

  useEffect(() => {
    detectLocationRef.current = detectLocation;
  });

  // The map was created once; this ref gives it the current panel handler.
  useEffect(() => {
    offerMapDragEnabledRef.current =
      offerRideOpen && homepageOfferStep < 3;

    rideMapClickRef.current = (latitude, longitude) => {
      if (goodsTransportOpen) return;

      // Lock Ride Now pickup/destination selection during an active request.
      // Keep map zoom, pan and live DRIVER tracking available.
      if (rideMode === "ride_now" && hasActivePassengerTrip) return;

      // Offer Ride map selection — Step 1 Pickup, Step 2 Destination.
      if (offerRideOpen) {
        if (homepageOfferStep === 1) {
          void showLocation(
            latitude,
            longitude,
            `Pickup (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`
          );
        } else if (homepageOfferStep === 2) {
          setOfferDestination({
            latitude,
            longitude,
            label: `Destination (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`,
          });
        }
        return;
      }

      if (!ridePanelOpen || rideMode === "explore") return;
      if (rideMode === "ride_now" && rideStep === 1) {
        void showLocation(latitude, longitude, `Pickup (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`);
        return;
      }

      if (rideMode === "ride_now" && rideStep === 2) {
        ++rideSearchSequenceRef.current;
        const label = `Destination (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`;
        setRideDestination({ latitude, longitude, label });
        setRideDestinationQuery(label);
        setRideDestinationSuggestions([]);
        setRideDestinationError("");
        setRideDestinationLoading(false);
        return;
      }

      if (rideMapPickMode === "pickup") {
        void showLocation(latitude, longitude, `Pickup (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`);
        setRideMapPickMode("destination");
        return;
      }

      ++rideSearchSequenceRef.current;
      const label = `Map point (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`;

      setRideDestination({ latitude, longitude, label });
      setRideDestinationQuery(label);
      setRideDestinationSuggestions([]);
      setRideDestinationError("");
      setRideDestinationLoading(false);
    };

    return () => {
      rideMapClickRef.current = () => {};
    };
  });

  // Show the destination on the SAME homepage Leaflet map.
  useEffect(() => {
    let cancelled = false;

    void import("leaflet").then((L) => {
      const map = leafletMapRef.current;
      if (cancelled || !map) return;

      rideDestinationMarkerRef.current?.remove();
      rideDestinationMarkerRef.current = null;

      if ((!ridePanelOpen && !hasActivePassengerTrip) || rideMode === "explore" || !rideDestination) return;

      const point: [number, number] = [
        rideDestination.latitude,
        rideDestination.longitude,
      ];

      rideDestinationMarkerRef.current = L.circleMarker(point, {
        radius: 9,
        color: "#ffffff",
        weight: 3,
        fillColor: "#dc2626",
        fillOpacity: 1,
      }).addTo(map).bindPopup("Ride destination");

      map.flyTo(point, Math.max(map.getZoom(), 12));
    }).catch((error) => {
      console.error("Destination marker unavailable:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [rideDestination, ridePanelOpen, rideMode, hasActivePassengerTrip]);

  // STEP202_NEARBY_DRIVERS: Approximate online-driver availability on Ride Now map.
  useEffect(() => {
    if (!sharedMap || !user || !((ridePanelOpen && rideMode === "ride_now") || (hasActivePassengerTrip && !hasDriverTrip))) return;

    let disposed = false;
    let layer: import("leaflet").LayerGroup | null = null;
    let currentRequest: AbortController | null = null;

    void import("leaflet").then((L) => {
      if (disposed) return;

      layer = L.layerGroup().addTo(sharedMap);

      async function refreshDrivers() {
        // Avoid cancelling an in-flight request on every map movement.
        if (currentRequest && !currentRequest.signal.aborted) return;

        const controller = new AbortController();
        currentRequest = controller;

        const center = ridePickup
          ? { lat: ridePickup.latitude, lng: ridePickup.longitude }
          : sharedMap!.getCenter();

        const params = new URLSearchParams({
          latitude: String(center.lat),
          longitude: String(center.lng),
          radiusKm: "10",
        });

        try {
          const response = await fetch(
            `/api/passenger/nearby-drivers?${params}`,
            { cache: "no-store", signal: controller.signal }
          );

          if (!response.ok) throw new Error("Driver availability unavailable");

          const result = await response.json() as {
            areas: Array<{ latitude: number; longitude: number; count: number }>;
            totalAvailable: number;
          };

          if (disposed || controller.signal.aborted || !layer) return;

          layer.clearLayers();

          for (const area of result.areas ?? []) {
            if (
              !Number.isFinite(area.latitude) ||
              !Number.isFinite(area.longitude) ||
              !Number.isFinite(area.count) ||
              area.count < 1
            ) continue;

            L.circleMarker([area.latitude, area.longitude], {
              radius: area.count > 1 ? 11 : 8,
              color: "#ffffff",
              weight: 3,
              fillColor: "#0d9488",
              fillOpacity: 0.9,
              bubblingMouseEvents: false,
            })
              .bindTooltip(
                `${area.count} online driver${area.count === 1 ? "" : "s"} nearby (approximate area)`
              )
              .addTo(layer);
          }
        } catch (error) {
          if (!controller.signal.aborted && !disposed) {
            // Temporary network failures must not erase the map.
            // Previously displayed nearby-driver markers are not
            // guaranteed to represent current online availability.
            console.warn("Nearby driver availability temporarily unavailable.", error);
          }
        } finally {
          if (currentRequest === controller) currentRequest = null;
        }
      }

      void refreshDrivers();

      const timer = window.setInterval(() => {
        void refreshDrivers();
      }, 10000);

      const onMove = () => {
        if (!ridePickup) void refreshDrivers();
      };

      sharedMap.on("moveend", onMove);

      cleanup = () => {
        window.clearInterval(timer);
        sharedMap.off("moveend", onMove);
      };
    }).catch((error) => {
      console.error("Driver map layer could not initialize:", error);
    });

    let cleanup = () => {};

    return () => {
      disposed = true;
      currentRequest?.abort();
      cleanup();
      layer?.remove();
    };
  }, [
    sharedMap,
    ridePanelOpen,
    rideMode,
    hasActivePassengerTrip,
    hasDriverTrip,
    user,
    ridePickup?.latitude,
    ridePickup?.longitude,
  ]);

  // Automatically suggest pickup locations while the user types.
  useEffect(() => {
    const query = ridePickupQuery.trim();

    if (!ridePanelOpen ||
        (rideMode !== "ride_now" && rideMode !== "destination") ||
        rideStep !== 1 || query.length < 3) {
      return;
    }

    const controller = new AbortController();

    const timer = setTimeout(async () => {
      setRidePickupLoading(true);
      setRidePickupError("");

      try {
        const response = await fetch(
          `${rideMode === "destination" ? "/api/location-search" : "/api/place-suggestions"}?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );

        if (!response.ok) throw new Error("Pickup suggestions unavailable");

        const results: unknown = await response.json();

        if (!Array.isArray(results)) throw new Error("Invalid pickup suggestions");

        const places: Array<{
          name: string;
          latitude: number;
          longitude: number;
        }> = [];

        for (const item of results) {
          if (
            !item ||
            typeof item.name !== "string" ||
            typeof item.latitude !== "number" ||
            typeof item.longitude !== "number" ||
            !Number.isFinite(item.latitude) ||
            !Number.isFinite(item.longitude)
          ) continue;

          places.push({
            name: item.name,
            latitude: item.latitude,
            longitude: item.longitude,
          });
        }

        if (!controller.signal.aborted) {
          setRidePickupSuggestions(places.slice(0, 8));
        }
      } catch {
        if (!controller.signal.aborted) {
          setRidePickupSuggestions([]);
          setRidePickupError("Could not load pickup suggestions.");
        }
      } finally {
        if (!controller.signal.aborted) setRidePickupLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [ridePickupQuery, ridePanelOpen, rideMode, rideStep]);

  // Search pickup locations using the existing Important Places and LGD API.
  async function searchRidePickups() {
    const query = ridePickupQuery.trim();

    if (query.length < 3) {
      setRidePickupError("Enter at least 3 characters.");
      return;
    }

    setRidePickupLoading(true);
    setRidePickupError("");
    setRidePickupSuggestions([]);

    try {
      const response = await fetch(
        `${rideMode === "destination" ? "/api/location-search" : "/api/place-suggestions"}?q=${encodeURIComponent(query)}`,
        { cache: "no-store" }
      );

      if (!response.ok) throw new Error("Pickup search unavailable");

      const results: unknown = await response.json();
      if (!Array.isArray(results)) throw new Error("Invalid pickup results");

      const places: Array<{
        name: string;
        latitude: number;
        longitude: number;
      }> = [];

      for (const item of results) {
        if (
          !item ||
          typeof item.name !== "string" ||
          typeof item.latitude !== "number" ||
          typeof item.longitude !== "number" ||
          !Number.isFinite(item.latitude) ||
          !Number.isFinite(item.longitude)
        ) continue;

        places.push({
          name: item.name,
          latitude: item.latitude,
          longitude: item.longitude,
        });
      }

      setRidePickupSuggestions(places.slice(0, 8));

      if (!places.length) {
        setRidePickupError("No pickup location found. Try the map or GPS.");
      }
    } catch {
      setRidePickupError("Could not search pickup. Please try again.");
    } finally {
      setRidePickupLoading(false);
    }
  }

  // Automatic destination suggestions for Existing Ride only.
  useEffect(() => {
    if (!ridePanelOpen || rideMode !== "destination" || rideStep !== 2) return;

    const query = rideDestinationQuery.trim();

    if (query.length < 3 || (rideDestination && rideDestinationQuery === rideDestination.label)) {
      return;
    }

    const controller = new AbortController();
    const requestId = ++rideSearchSequenceRef.current;

    const timer = setTimeout(async () => {
      setRideDestinationLoading(true);
      setRideDestinationError("");

      try {
        const response = await fetch(
          `/api/location-search?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );

        if (!response.ok) throw new Error("Suggestions unavailable");

        const results: unknown = await response.json();
        if (!Array.isArray(results)) throw new Error("Invalid suggestions");

        const places: Array<{name: string; latitude: number; longitude: number}> = [];

        for (const item of results) {
          if (
            !item ||
            typeof item.name !== "string" ||
            typeof item.latitude !== "number" ||
            typeof item.longitude !== "number" ||
            !Number.isFinite(item.latitude) ||
            !Number.isFinite(item.longitude)
          ) continue;

          places.push({
            name: item.name,
            latitude: item.latitude,
            longitude: item.longitude,
          });
        }

        if (!controller.signal.aborted && requestId === rideSearchSequenceRef.current) {
          setRideDestinationSuggestions(places.slice(0, 8));
        }
      } catch {
        if (!controller.signal.aborted && requestId === rideSearchSequenceRef.current) {
          setRideDestinationSuggestions([]);
          setRideDestinationError("Could not load destination suggestions.");
        }
      } finally {
        if (!controller.signal.aborted && requestId === rideSearchSequenceRef.current) {
          setRideDestinationLoading(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [rideDestinationQuery, ridePanelOpen, rideMode, rideStep, rideDestination]);

  // Reuse the existing LGD villages and Important Places search.
  async function searchRideDestinations() {
    const query = rideDestinationQuery.trim();

    if (query.length < 3) {
      setRideDestinationError("Enter at least 3 characters.");
      return;
    }

    const requestId = ++rideSearchSequenceRef.current;

    setRideDestinationLoading(true);
    setRideDestinationError("");
    setRideDestinationSuggestions([]);

    try {
      const response = await fetch(
        `${rideMode === "destination" ? "/api/location-search" : "/api/place-suggestions"}?q=${encodeURIComponent(query)}`,
        { cache: "no-store" }
      );

      if (!response.ok) throw new Error("Search unavailable");

      const result: unknown = await response.json();
      if (!Array.isArray(result)) throw new Error("Invalid search results");

      if (requestId !== rideSearchSequenceRef.current) return;

      const mapped: Array<{
        name: string;
        latitude: number;
        longitude: number;
      }> = [];

      for (const item of result) {
        if (
          !item ||
          typeof item.name !== "string" ||
          typeof item.latitude !== "number" ||
          typeof item.longitude !== "number" ||
          !Number.isFinite(item.latitude) ||
          !Number.isFinite(item.longitude)
        ) continue;

        mapped.push({
          name: item.name,
          latitude: item.latitude,
          longitude: item.longitude,
        });
      }

      setRideDestinationSuggestions(mapped.slice(0, 8));

      if (!mapped.length) {
        setRideDestinationError(
          "No destination with map coordinates found. Try tapping the map."
        );
      }
    } catch {
      if (requestId === rideSearchSequenceRef.current) {
        setRideDestinationError("Destination search failed. Try again.");
      }
    } finally {
      if (requestId === rideSearchSequenceRef.current) {
        setRideDestinationLoading(false);
      }
    }
  }

  // Plot available ride pickup points on the existing homepage map.
  useEffect(() => {
    let cancelled = false;

    void import("leaflet").then((L) => {
      const map = leafletMapRef.current;
      if (cancelled || !map) return;

      compactRideMarkersRef.current?.remove();
      compactRideMarkersRef.current = null;

      if (!ridePanelOpen || rideMode !== "destination") return;

      const layer = L.layerGroup().addTo(map);
      compactRideMarkersRef.current = layer;

      for (const ride of compactRideRows) {
        if (
          typeof ride.latitude !== "number" ||
          typeof ride.longitude !== "number" ||
          !Number.isFinite(ride.latitude) ||
          !Number.isFinite(ride.longitude) ||
          Math.abs(ride.latitude) > 90 ||
          Math.abs(ride.longitude) > 180
        ) continue;

        const marker = L.circleMarker(
          [ride.latitude, ride.longitude],
          {
            radius: 6,
            bubblingMouseEvents: false,
            color: "#ffffff",
            weight: 2,
            fillColor: "#4f46e5",
            fillOpacity: 0.95,
          }
        ).addTo(layer);

        // Text content prevents user-provided HTML injection.
        const popup = document.createElement("div");
        popup.textContent =
          `${ride.name}: ${ride.from} → ${ride.to} · ${ride.seats} seats`;

        marker.bindPopup(popup);
        marker.on("click", () => setSelectedCompactRideId(ride.id));
      }
    }).catch((error) => {
      console.error("Ride pickup markers unavailable:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [compactRideRows, ridePanelOpen, rideMode]);

  // Fetch minimum ride details only while Find Ride is open.
  useEffect(() => {
    // Clear old selection whenever pickup or destination changes.
    setSelectedCompactRideId(null);

    // STEP376_ROUTE_MATCHING: Capture coordinates before async work.
    if (
      !ridePanelOpen ||
      rideMode !== "destination" ||
      !ridePickup ||
      !rideDestination ||
      !existingRideDate ||
      !existingRideResultsOpen
    ) {
      setCompactRideRows([]);
      setCompactRideLoading(false);
      setCompactRideError("");
      setCompactRideNeedsLogin(false);
      return;
    }

    const pickupLat = ridePickup.latitude;
    const pickupLng = ridePickup.longitude;
    const dropLat = rideDestination.latitude;
    const dropLng = rideDestination.longitude;

    const controller = new AbortController();

    async function loadCompactRides() {
      setCompactRideLoading(true);
      setCompactRideError("");
      setCompactRideNeedsLogin(false);

      try {
        const params = new URLSearchParams({
          pickupLat: String(pickupLat),
          pickupLng: String(pickupLng),
          dropLat: String(dropLat),
          dropLng: String(dropLng),
          date: existingRideDate,
        });

        const response = await fetch(`/api/rides/compact?${params}`, {
          cache: "no-store",
          signal: controller.signal,
        });

        if (response.status === 401) {
          if (!controller.signal.aborted) {
            setCompactRideRows([]);
            setCompactRideNeedsLogin(true);
          }
          return;
        }

        if (!response.ok) {
          throw new Error("Unable to load rides");
        }

        const result = await response.json() as {
          rides?: CompactRide[];
        };

        if (!controller.signal.aborted) {
          setCompactRideRows(
            Array.isArray(result.rides) ? result.rides : []
          );
        }
      } catch {
        if (!controller.signal.aborted) {
          setCompactRideRows([]);
          setCompactRideError("Available rides could not be loaded.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setCompactRideLoading(false);
        }
      }
    }

    void loadCompactRides();

    return () => controller.abort();
  }, [ridePanelOpen, rideMode, ridePickup, rideDestination, existingRideDate, existingRideResultsOpen]);

  function openRideMode(mode: "ride_now" | "destination" | "explore" | "scheduled") {
    ++rideSearchSequenceRef.current;
    if (mode === "ride_now" || mode === "destination") {
      setRideStep(1);
      setRideMapPickMode("pickup");
    }
    if (mode === "destination") {
      setExistingRideDate("");
      setExistingRideResultsOpen(false);
    }
    setRideMode(mode);
    setSelectedCompactRideId(null);
    setRideDestinationSuggestions([]);
    setRideDestinationError("");
    setRideDestinationLoading(false);
    setFoodCustomersOpen(false);
    setPassengersOpen(false);
    setGoodsTransportOpen(false);
    setDriverOpen(false);
    setRidePanelOpen(true);
  }

  function openPassengerRadar() {
    ++rideSearchSequenceRef.current;
    setRidePanelOpen(false);
    setFoodCustomersOpen(false);
    setGoodsTransportOpen(false);
    setSelectedCompactRideId(null);
    setDriverOpen(false);
    setPassengersOpen(true);
  }

  // Keep the booking controls visible while DRIVER has an active trip.
  useEffect(() => {
    if (!hasDriverTrip) return;

    setDriverOpen(true);
    setRidePanelOpen(false);
    setPassengersOpen(false);
    setFoodCustomersOpen(false);
    setGoodsTransportOpen(false);
    setMenuOpen(false);
  }, [hasDriverTrip]);


  // Open Offer Ride from the approved Passenger DRIVER controls.
  useEffect(() => {
    const handleOpenOfferRide = () => {
      ++rideSearchSequenceRef.current;
      setRidePanelOpen(false);
      setDriverOpen(false);
      setGoodsTransportOpen(false);
      setPassengersOpen(false);
      setFoodCustomersOpen(false);
      setSelectedCompactRideId(null);
      setOfferRideOpen(true);
      setMenuOpen(false);
    };

    window.addEventListener("apnasaathi:open-offer-ride", handleOpenOfferRide);
    return () => {
      window.removeEventListener("apnasaathi:open-offer-ride", handleOpenOfferRide);
    };
  }, []);

  // Open DRIVER radar only after a successful Passenger Go Online.
  useEffect(() => {
    const handleDriverOnline = () => {
      openDriverPanel();
    };

    window.addEventListener("apnasaathi:passenger-driver-online", handleDriverOnline);

    return () => {
      window.removeEventListener("apnasaathi:passenger-driver-online", handleDriverOnline);
    };
  }, []);


  useEffect(() => {
    function handleAvailabilityChanged(event: Event) {
      const detail = (event as CustomEvent<{
        category: "Passenger" | "Goods";
        online: boolean;
      }>).detail;

      if (!detail || (detail.category !== "Passenger" && detail.category !== "Goods")) return;

      setDriverOnlineCategory(detail.online ? detail.category : null);

      if (detail.online && detail.category === "Passenger") {
        openDriverPanel();
      }
    }

    window.addEventListener("apnasaathi:driver-availability-changed", handleAvailabilityChanged);
    return () => window.removeEventListener("apnasaathi:driver-availability-changed", handleAvailabilityChanged);
  }, []);

  function openDriverPanel() {
    ++rideSearchSequenceRef.current;
    setRidePanelOpen(false);
    setFoodCustomersOpen(false);
    setPassengersOpen(false);
    setGoodsTransportOpen(false);
    setSelectedCompactRideId(null);
    setDriverOpen(true);
  }

  async function toggleHomepageFoodAvailability() {
    if (!user || !approvedFoodSellerName || updatingFoodSellerAvailability) return;

    const nextAvailability = !foodSellerAvailable;

    try {
      setUpdatingFoodSellerAvailability(true);

      const response = await fetch("/api/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          foodSellerAvailable: nextAvailability,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.message || "Unable to update Food availability.");
        return;
      }

      if (typeof data.foodSellerAvailable !== "boolean") {
        alert("Server did not confirm Food availability.");
        return;
      }

      setFoodSellerAvailable(data.foodSellerAvailable);
    } catch (error) {
      console.error("Homepage Food availability update failed:", error);
      alert("Unable to update Food availability.");
    } finally {
      setUpdatingFoodSellerAvailability(false);
    }
  }

  async function logout() {
    setLoggingOut(true);
    try { await fetch("/api/logout", { method: "POST" }); }
    catch (error) { console.error("Logout error:", error); }
    finally {
      localStorage.removeItem("apnasaathiUser");
      setUser(null);
      setUnreadCount(0);
      setIsAdmin(false);
      window.location.href = "/login";
    }
  }

  return (
    <main className="relative h-[100dvh] min-h-[480px] overflow-hidden bg-slate-100 text-slate-900">
      <header className="absolute inset-x-0 top-0 z-[1000] border-b border-white/70 bg-white/95 pt-[max(env(safe-area-inset-top),20px)] shadow-sm backdrop-blur sm:pt-0">
        <div className="mx-auto flex h-16 max-w-5xl items-center gap-2 px-4 sm:px-6">
          <Link href="/" className="flex min-w-0 max-w-40 shrink flex-col items-start gap-0.5" aria-label="ApnaSaathi home">
            <img src="/asr-logo.png" alt="ASR ApnaSaathi logo" className="h-9 w-10 shrink-0 rounded-lg object-contain" />
            <span className="flex w-full min-w-0 items-center gap-1 text-[10px] font-medium text-slate-600" title={locationName}>
              <MapPin className="h-3 w-3 shrink-0 text-emerald-600" />
              <span className="truncate">{locationName}</span>
            </span>
          </Link>
          {/* Approved Food Seller header control */}
          {approvedFoodSellerName && (
            <div className="flex w-[90px] shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl border border-orange-200 bg-orange-50 px-1 py-1 sm:w-[115px]">
              <span
                className="w-full truncate text-center text-[11px] font-extrabold text-slate-800"
                title={approvedFoodSellerName}
              >
                {approvedFoodSellerName}
              </span>
              <button
                type="button"
                onClick={toggleHomepageFoodAvailability}
                disabled={updatingFoodSellerAvailability}
                aria-pressed={foodSellerAvailable}
                className={`w-full rounded-full px-1 py-1 text-[10px] font-bold disabled:opacity-50 ${
                  foodSellerAvailable
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-slate-200 text-slate-700"
                }`}
              >
                {updatingFoodSellerAvailability
                  ? "Updating..."
                  : foodSellerAvailable
                    ? "🟢 Open"
                    : "⚪ Closed"}
              </button>
            </div>
          )}
          <div className="flex shrink-0 flex-col items-center gap-0.5">
      {/* Keep exactly one DRIVER GPS/session controller mounted across panel switches. */}
      <div className={`pointer-events-auto "relative z-[1100] w-auto shrink-0" ${
        hasDriverTrip || hasActivePassengerTrip ||
        goodsTransportOpen || passengersOpen || foodCustomersOpen || ridePanelOpen || offerRideOpen
          ? "hidden"
          : ""
      }`}>
        <HomepageDriverSessionContext.Provider value={driverSessionContextValue}>
          <HomepageDriverTileAvailability loggedIn={!!user} compact />
        </HomepageDriverSessionContext.Provider>
      </div>
          </div>
          <div className={`ml-auto flex items-center gap-1.5 sm:gap-2 ${(hasDriverTrip || hasActivePassengerTrip) ? "hidden" : ""}`}>
            {user && (
              <Link href="/notifications" aria-label="Notifications" className="relative rounded-lg p-2.5 hover:bg-slate-100">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && <span className="absolute right-0 top-0 flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">{unreadCount > 99 ? "99+" : unreadCount}</span>}
              </Link>
            )}
            {user ? <Link href="/profile" aria-label="Profile" className="flex max-w-24 flex-col items-center rounded-lg px-1 py-1 hover:bg-slate-100">
              <User className="h-5 w-5 shrink-0" />
              <span className="max-w-full truncate text-[10px] font-medium text-indigo-600">{user.name.split(" ")[0]}</span>
            </Link>
              : <Link href="/login" className="rounded-lg px-3 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50">Login</Link>}
            <div ref={menuRef} className="relative">
              <button type="button" aria-label="Open services menu" aria-expanded={menuOpen} aria-controls="home-services-menu"
                onClick={() => setMenuOpen(v => !v)} className="rounded-lg p-2.5 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600">
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              {menuOpen && (
                <div id="home-services-menu" className="absolute right-0 top-12 z-50 max-h-[min(75vh,600px)] w-[min(88vw,320px)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
                  {user && (
                      <Link
                        href="/my-rides"
                        aria-label="Open My Rides"
                        onClick={() => setMenuOpen(false)}
                        className="mb-2 flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-3 text-sm font-bold text-indigo-700 hover:bg-indigo-100"
                      >
                        <CarFront className="h-4 w-4" />
                        My Rides
                      </Link>
                    )}
                    <p className="px-2 pb-2 text-sm font-semibold">All Services</p>
                  {menuSections.map(section => (
                    <details key={section.title} className="group border-t border-slate-100 py-1" open={section.title === "Rides" ? true : undefined}>
                      <summary className="cursor-pointer list-none rounded-lg px-2 py-2.5 text-sm font-semibold hover:bg-slate-50">{section.title}<span className="float-right text-slate-400">⌄</span></summary>
                      <div className="pb-1">
                        {section.links.map(({ label, href, icon: Icon }) => (
                          <Link key={href} href={href} onClick={(event) => {
                            setMenuOpen(false);
                            if (href === "/find-ride") {
                              event.preventDefault();
                              openRideMode("ride_now");
                            } else if (href === "/ride-requests") {
                              event.preventDefault();
                              openPassengerRadar();
                            } else if (href === "/driver") {
                              event.preventDefault();
                              openDriverPanel();
                            } else if (href === "/offer-ride") {
                              event.preventDefault();
                              ++rideSearchSequenceRef.current;
                              setRidePanelOpen(false);
                              setDriverOpen(false);
                              setGoodsTransportOpen(false);
                              setPassengersOpen(false);
                              setFoodCustomersOpen(false);
                              setSelectedCompactRideId(null);
                              setOfferRideOpen(true);
                            } else if (href === "/buy-food") {
                              event.preventDefault();
                              setMobileFoodMenuOpen(false);
                              setFindFoodOpen(true);
                            } else if (href === "/food/find-customers") {
                              event.preventDefault();
                              setRidePanelOpen(false);
                              setPassengersOpen(false);
                              setFoodCustomersOpen(true);
                            }
                          }} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-indigo-600">
                            <Icon className="h-4 w-4" />{label}
                          </Link>
                        ))}
                      </div>
                    </details>
                  ))}
                  {user && <div className="mt-2 border-t border-slate-100 pt-2">
                    <Link href="/my-activity" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm hover:bg-slate-50"><Activity className="h-4 w-4" /> My Activity</Link>
                    {isAdmin && <Link href="/admin" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm hover:bg-slate-50"><ShieldCheck className="h-4 w-4" /> Admin</Link>}
                    <button onClick={logout} disabled={loggingOut} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"><LogOut className="h-4 w-4" /> {loggingOut ? "Logging out..." : "Logout"}</button>
                  </div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {/* Second header — quick services */}
      {!hasDriverTrip && !hasActivePassengerTrip && (
        <nav
          aria-label="Driver and food quick actions"
          className="absolute inset-x-0 top-[calc(64px+max(env(safe-area-inset-top),20px))] z-[900] max-h-[calc(100dvh-11rem)] overflow-y-auto overscroll-contain border-b border-emerald-100 bg-[#eff9eb]/95 px-3 py-3 shadow-sm backdrop-blur sm:top-16"
        >
          {!(
            driverOpen || offerRideOpen || goodsTransportOpen ||
            passengersOpen || foodCustomersOpen || ridePanelOpen ||
            driverOnlineCategory
          ) && (
            <div className="mx-auto mb-3 max-w-5xl rounded-3xl bg-[#BEF400] p-4 text-[#142518] shadow-sm sm:p-5">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-extrabold tracking-wider">
                  APNASAATHI SUPER APP
                </span>
                <span aria-hidden="true" className="text-xl">✨</span>
              </div>
              <h2 className="max-w-sm text-xl font-black leading-tight sm:text-2xl">
                Move, Share &amp; Shop with ApnaSaathi
              </h2>
              <p className="mt-2 text-xs font-medium sm:text-sm">
                Rides • Food • Marketplace
              </p>
              <button
                type="button"
                onClick={() =>
                  document.getElementById("homepage-quick-services")?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  })
                }
                className="mt-4 rounded-full bg-[#142518] px-5 py-3 text-xs font-bold text-white transition hover:bg-[#29451C]"
              >
                Explore Services →
              </button>
            </div>
          )}
          <div id="homepage-quick-services" className="mx-auto grid max-w-5xl grid-cols-4 gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => {
                setMobileRideMenuOpen(false);
                openDriverPanel();
              }}
              className="min-h-14 rounded-2xl border border-white bg-white px-1 py-2 text-[10px] font-bold text-emerald-950 shadow-sm transition hover:bg-lime-100 sm:text-sm"
            >
              <CarFront className="mx-auto mb-1 h-6 w-6 text-emerald-800" />
              <span className="block">DRIVER</span>
            </button>
            <button
              type="button"
              onClick={() => {
                ++rideSearchSequenceRef.current;
                setMobileRideMenuOpen(false);
                setRidePanelOpen(false);
                setDriverOpen(false);
                setGoodsTransportOpen(false);
                setPassengersOpen(false);
                setFoodCustomersOpen(false);
                setSelectedCompactRideId(null);
                setOfferRideOpen(true);
              }}
              className="min-h-14 rounded-2xl border border-white bg-white px-1 py-2 text-[10px] font-bold text-emerald-950 shadow-sm transition hover:bg-lime-100 sm:text-sm"
            >
              <CarFront className="mx-auto mb-1 h-6 w-6 text-emerald-800" />
              <span className="block">Offer Ride</span>
            </button>
            <Link
              href="/sell-food"
              className="min-h-14 rounded-2xl border border-white bg-white px-1 py-2 text-center text-[10px] font-bold text-emerald-950 shadow-sm transition hover:bg-lime-100 sm:text-sm"
            >
              <Utensils className="mx-auto mb-1 h-6 w-6 text-emerald-800" />
              <span className="block">Sell Food</span>
            </Link>
            <button
              type="button"
              onClick={() => {
                setMobileRideMenuOpen(false);
                setRidePanelOpen(false);
                setDriverOpen(false);
                setOfferRideOpen(false);
                setGoodsTransportOpen(false);
                setPassengersOpen(false);
                setFoodCustomersOpen(true);
              }}
              className="min-h-14 rounded-2xl border border-white bg-white px-1 py-2 text-[10px] font-bold text-emerald-950 shadow-sm transition hover:bg-lime-100 sm:text-sm"
            >
              <Users className="mx-auto mb-1 h-6 w-6 text-emerald-800" />
              <span className="block">Find Customer</span>
            </button>
          </div>
      {/* ApnaSaathi Mobile Service Navigation */}
      {!ridePanelOpen &&
       !offerRideOpen &&
       !passengersOpen &&
       !foodCustomersOpen &&
       !goodsTransportOpen &&
       !hasDriverTrip &&
       !hasActivePassengerTrip && (
        <nav
          aria-label="Mobile services"
          className="relative z-[1] mx-auto mt-3 w-full max-w-5xl bg-transparent p-0"
        >
          {mobileRideMenuOpen && (
            <div
              id="mobile-ride-options"
              className="mx-auto mb-3 flex max-w-md gap-2 overflow-x-auto rounded-2xl border border-indigo-100 bg-white p-2 shadow-lg"
            >
              {[
                { label: "Ride Now", action: () => openRideMode("ride_now") },
                { label: "Search Existing Ride", action: () => openRideMode("destination") },
                { label: "Nearby Ride", action: () => openRideMode("explore") },
                { label: "Scheduled Request", action: () => openRideMode("scheduled") },
                { label: "Goods Transport", action: () => {
                  ++rideSearchSequenceRef.current;
                  setRidePanelOpen(false);
                  setDriverOpen(false);
                  setOfferRideOpen(false);
                  setPassengersOpen(false);
                  setFoodCustomersOpen(false);
                  setSelectedCompactRideId(null);
                  setGoodsTransportOpen(true);
                } },
              ].map(({ label, action }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setMobileRideMenuOpen(false);
                    action();
                  }}
                  className="shrink-0 whitespace-nowrap rounded-xl bg-indigo-50 px-3 py-3 text-xs font-bold text-indigo-800 hover:bg-indigo-100"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {mobileFoodMenuOpen && (
            <div
              id="mobile-food-options"
              className="mx-auto mb-3 grid max-w-md grid-cols-2 gap-2 rounded-2xl border border-green-200 bg-white p-2 shadow-lg"
            >
              <Link
                href="/buy-food"
                onClick={(event) => {
                  event.preventDefault();
                  setMobileFoodMenuOpen(false);
                  setFindFoodOpen(true);
                }}
                className="flex-1 rounded-xl bg-green-50 px-3 py-3 text-center text-xs font-bold text-green-800"
              >
                🍽️ Find Food
              </Link>
              <Link
                href="/orders"
                onClick={() => setMobileFoodMenuOpen(false)}
                className="flex-1 rounded-xl bg-green-50 px-3 py-3 text-center text-xs font-bold text-green-800"
              >
                🛒 My Food Orders
              </Link>
              <Link
                href="/request-food"
                onClick={() => setMobileFoodMenuOpen(false)}
                className="flex-1 rounded-xl bg-green-50 px-3 py-3 text-center text-xs font-bold text-green-800"
              >
                📝 Request Food
              </Link>
              <Link
                href="/my-food-requests"
                onClick={() => setMobileFoodMenuOpen(false)}
                className="flex-1 rounded-xl bg-green-50 px-3 py-3 text-center text-xs font-bold text-green-800"
              >
                📋 My Food Requests
              </Link>
            </div>
          )}
          <div className="mx-auto grid w-full max-w-5xl grid-cols-4 gap-2 sm:gap-4">
            <button
              type="button"
              aria-expanded={mobileRideMenuOpen}
              aria-controls="mobile-ride-options"
              onClick={() => {
                setMobileFoodMenuOpen(false);
                setMobileRideMenuOpen(v => !v);
              }}
              className="flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl border border-white bg-white px-1 py-2 text-[10px] font-bold text-emerald-950 shadow-sm transition hover:bg-lime-100 sm:text-sm"
            >
              <CarFront className="h-6 w-6" />
              <span>Ride</span>
            </button>
            <button
              type="button"
              aria-expanded={mobileFoodMenuOpen}
              aria-controls="mobile-food-options"
              onClick={() => {
                setMobileRideMenuOpen(false);
                setMobileFoodMenuOpen(v => !v);
              }}
              className="flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl border border-white bg-white px-1 py-2 text-[10px] font-bold text-emerald-950 shadow-sm transition hover:bg-lime-100 sm:text-sm"
            >
              <Utensils className="h-6 w-6" />
              <span>Food</span>
            </button>
            {[
              
              { label: "Marketplace", href: "/marketplace", Icon: Store },
            ].map(({ label, href, Icon }) => (
              <Link
                key={href}
                href={href}
                className={`flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl border border-white bg-white px-1 py-2 text-[10px] font-bold shadow-sm transition hover:bg-lime-100 sm:text-sm ${
                  href === "/" ? "text-emerald-950" : "text-emerald-950"
                }`}
              >
                <Icon className="h-6 w-6" />
                <span>{label}</span>
              </Link>
            ))}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl border border-white bg-white px-1 py-2 text-[10px] font-bold text-emerald-950 shadow-sm transition hover:bg-lime-100 sm:text-sm"
            >
              <Users className="h-6 w-6" />
              <span>Community</span>
            </button>
          </div>
        </nav>
      )}

        </nav>
      )}

      {!(
        driverOnlineCategory || driverOpen || hasDriverTrip ||
        hasActivePassengerTrip || ridePanelOpen || offerRideOpen ||
        goodsTransportOpen || passengersOpen || foodCustomersOpen
      ) && (
        <section
          id="homepage-marketplace-promo"
          aria-label="Marketplace promotion"
          className="pointer-events-auto absolute inset-x-0 bottom-0 top-[min(28rem,60dvh)] z-[800] overflow-y-auto bg-[#BEF400] px-3 py-5 sm:px-6"
        >
          <div className="mx-auto max-w-5xl rounded-3xl bg-[#BEF400] p-5 shadow-lg sm:p-8">
            <p className="text-xs font-extrabold tracking-widest text-emerald-700">
              APNASAATHI MARKETPLACE
            </p>
            <h2 className="mt-2 text-2xl font-black text-emerald-950">
              Direct from Store, Straight to Your Door!
            </h2>
            <p className="mt-2 text-sm text-emerald-800">
              Shop ACs, TVs, Electronics &amp; Home Appliances.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-lime-100 p-5 text-center text-sm font-bold text-emerald-950">
                ❄️ Air Conditioners
              </div>
              <div className="rounded-2xl bg-emerald-100 p-5 text-center text-sm font-bold text-emerald-950">
                📺 TVs &amp; Electronics
              </div>
            </div>
            <Link
              href="/marketplace"
              className="mt-5 inline-flex rounded-full bg-[#142518] px-6 py-3 text-sm font-bold text-white hover:bg-emerald-800"
            >
              Shop Marketplace →
            </Link>
          </div>
        </section>
      )}

      <div ref={mapRef} className="absolute inset-0 z-0 !cursor-default [&.leaflet-grab]:!cursor-default [&.leaflet-dragging]:!cursor-default [&_.leaflet-interactive]:!cursor-default" role="application" aria-label="Interactive map of nearby area" />

      <div className={`pointer-events-none absolute inset-x-0 z-[500] px-3 sm:px-6 ${
          ridePanelOpen && rideMode === "ride_now"
            ? "bottom-4 sm:bottom-6"
            : offerRideOpen
              ? "bottom-2 sm:bottom-4"
              : "top-20"
        }`}>
        <div className="pointer-events-auto mx-auto max-w-xl rounded-2xl border border-white/60 bg-white/70 p-2.5 shadow-lg backdrop-blur-xl sm:p-3">
          <div className={`max-h-[42dvh] space-y-2 overflow-y-auto ${
            ridePanelOpen && rideMode === "ride_now" && rideStep === 3
              ? ""
              : hasPassengerTrip ? "" : "hidden"
          }`}>
              {ridePanelOpen && rideMode === "ride_now" && rideStep === 3 && !hasActivePassengerTrip && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-indigo-800">
                  Ride Details · Step 3/3
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setRideStep(2);
                      setRideMapPickMode("destination");
                    }}
                    className="rounded-lg border border-indigo-200 bg-white/70 px-2 py-1 text-xs font-semibold"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    aria-label="Close Ride Now"
                    onClick={() => {
                      setRidePanelOpen(false);
                      setRideStep(1);
                      setSearchEditing(false);
                      setSearchQuery("");
                      setSuggestionsOpen(false);
                    }}
                    className="rounded-lg border border-indigo-200 bg-white/70 px-2 py-1 text-xs font-semibold"
                  >
                    ×
                  </button>
                </div>
              </div>
              )}
              <PassengerRideRequestPanel
                mode="Now"
                pickup={ridePickup}
                destination={rideDestination}
                map={sharedMap}
                loggedIn={!!user}
                showForm={ridePanelOpen && rideMode === "ride_now" && rideStep === 3}
                onTripVisibilityChange={setHasPassengerTrip}
                onActiveTripChange={setHasActivePassengerTrip}
                onRestoreActiveRide={(trip) => {
                  if (trip.fromLatitude != null && trip.fromLongitude != null &&
                      trip.toLatitude != null && trip.toLongitude != null) {
                    setRidePickup({
                      latitude: trip.fromLatitude,
                      longitude: trip.fromLongitude,
                      label: trip.fromLocation,
                    });
                    setRideDestination({
                      latitude: trip.toLatitude,
                      longitude: trip.toLongitude,
                      label: trip.toLocation,
                    });
                    setRideDestinationQuery(trip.toLocation);
                  }
                }}
                onSearchEnded={() => {
                  setRidePanelOpen(false);
                  setRideStep(1);
                  setSearchEditing(false);
                  setSearchQuery("");
                  setSuggestionsOpen(false);
                }}
              />
            </div>
          <div className={(hasDriverTrip || hasActivePassengerTrip || (ridePanelOpen && rideMode === "destination") || (ridePanelOpen && rideMode === "ride_now" && rideStep === 3)) ? "hidden" : ""}>
          <form onSubmit={searchPlace} className="flex items-center gap-2">
            <Search className="h-5 w-5 shrink-0 text-indigo-600" />
            <input
              value={
                searchEditing
                  ? searchQuery
                  : offerRideOpen && homepageOfferStep === 2
                    ? (offerDestination?.label ?? "")
                  : ridePanelOpen && rideMode === "ride_now" && rideStep === 2
                    ? (rideDestination?.label ?? "")
                    : locationName
              }
              onChange={(event) => {
                setSearchEditing(true);
                setSearchQuery(event.target.value);
                setSuggestions([]);
                setSuggestionsOpen(true);
                setLocationError("");
              }}
              onFocus={(event) => {
                if (!searchEditing && locationName) {
                  event.currentTarget.select();
                }
                if (searchQuery.trim().length >= 3) setSuggestionsOpen(true);
              }}
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={suggestionsOpen && searchQuery.trim().length >= 3}
              aria-controls="location-suggestions"
              placeholder={locationName || "Search area, city, landmark or address"}
              title={locationName}
              aria-label="Search for a location"
              className="min-w-0 flex-1 bg-transparent py-2 text-sm outline-none placeholder:text-slate-400"
            />
            <button type="submit" disabled={searching || searchQuery.trim().length < 3}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {searching ? "Searching..." : "Search"}
            </button>
            <button id="auto-detect-location" type="button" onClick={detectLocation} disabled={gettingLocation}
              title="Detect my current location" aria-label="Locate me"
              className="rounded-lg border border-indigo-200 bg-indigo-50 p-2 text-indigo-700 disabled:opacity-50">
              {gettingLocation ? <Loader2 className="h-5 w-5 animate-spin" /> : <MapPin className="h-5 w-5" />}
            </button>
          </form>
          {suggestionsOpen && searchQuery.trim().length >= 3 && (
            <div
              id="location-suggestions"
              className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
            >
              {suggestionsLoading && (
                <p className="px-3 py-3 text-sm text-slate-500">
                  Finding locations...
                </p>
              )}
              {!suggestionsLoading && suggestions.map((place, index) => (
                <button
                  key={`${place.lat}-${place.lon}-${index}`}
                  type="button"
                  onClick={() => {
                    setSuggestionsOpen(false);
                    setSuggestions([]);
                    setSearchQuery("");
                    setLocationError("");
                    if (place.lat !== null && place.lon !== null) {
                      void showLocation(
                        Number(place.lat),
                        Number(place.lon),
                        place.display_name
                      );
                    } else {
                      ++locationSelectionRef.current;
                      setSearchEditing(false);
                      setLocationName(place.display_name);
                      setLocationDetails(
                        `LGD village code: ${place.villageCode ?? "Unavailable"}`
                      );
                      setRidePickup(null);
                      locationMarkerRef.current?.remove();
                      locationMarkerRef.current = null;
                      setLocationError(
                        "Village found in the LGD directory. Verified map coordinates are not yet available, so the map has not moved."
                      );
                    }
                  }}
                  className="flex w-full items-start gap-2 border-b border-slate-100 px-3 py-3 text-left text-sm text-slate-700 last:border-b-0 hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
                  <span className="min-w-0 break-words">
                    <span className="block">{place.display_name}</span>
                    {place.type === "village" && (
                      <span className="block text-xs text-slate-500">
                        LGD: {place.villageCode}
                        {place.lat === null || place.lon === null
                          ? " · Map coordinates pending"
                          : " · Map location available"}
                      </span>
                    )}
                  </span>
                </button>
              ))}
              {!suggestionsLoading && suggestions.length === 0 && (
                <p className="px-3 py-3 text-sm text-slate-500">
                  No suggestions yet. You can also press Search.
                </p>
              )}
            </div>
          )}
          {ridePanelOpen && rideMode === "ride_now" && rideStep === 2 && (
            <div className="mt-2 rounded-lg bg-white/50 px-2 py-1.5">
              <RideRoutePreview
                pickup={ridePickup}
                destination={rideDestination}
                map={sharedMap}
              />
            </div>
          )}

          {offerRideOpen && homepageOfferStep < 3 && (

            <div className="mt-2 flex items-center justify-between gap-2 border-t border-white/50 pt-2">

              <span className="min-w-0 truncate text-xs font-semibold text-slate-700">

                {homepageOfferStep === 1

                  ? "Offer Ride · Step 1/3 · Pickup Point"

                  : "Offer Ride · Step 2/3 · Destination Point"}

              </span>

              <div className="flex shrink-0 gap-2">

                <button type="button" onClick={() => {

                  setOfferRideOpen(false);

                  setHomepageOfferStep(1);

                }} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold">

                  ×

                </button>

                {homepageOfferStep === 2 && (

                  <button type="button" onClick={() => {

                    setHomepageOfferStep(1);

                    setSearchEditing(false);

                    setSearchQuery("");

                    setSuggestionsOpen(false);

                  }} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold">

                    Back

                  </button>

                )}

                <button type="button"

                  disabled={homepageOfferStep === 1 ? !ridePickup : !offerDestination}

                  onClick={() => {

                    setHomepageOfferStep(homepageOfferStep === 1 ? 2 : 3);

                    setSearchEditing(false);

                    setSearchQuery("");

                    setSuggestionsOpen(false);

                  }}

                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">

                  Next →

                </button>

              </div>

            </div>

          )}


          {ridePanelOpen && rideMode === "ride_now" && rideStep < 3 && (
            <div className="mt-2 flex items-center justify-between gap-2 border-t border-white/50 pt-2">
              <span className="min-w-0 truncate text-xs font-semibold text-slate-700">
                {rideStep === 1 ? "1/3 · Pickup Point" : "2/3 · Destination Point"}
              </span>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  aria-label="Close Ride Now"
                  title="Close Ride Now"
                  onClick={() => {
                    ++rideSearchSequenceRef.current;
                    setRidePanelOpen(false);
                    setRideStep(1);
                    setSearchEditing(false);
                    setSearchQuery("");
                    setSuggestions([]);
                    setSuggestionsOpen(false);
                    setLocationError("");
                  }}
                  className="rounded-lg border border-white/60 bg-white/60 px-2.5 py-1.5 text-xs font-bold text-slate-700"
                >
                  ×
                </button>
                {rideStep === 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      setRideStep(1);
                      setRideMapPickMode("pickup");
                      setSearchEditing(false);
                      setSearchQuery("");
                      setSuggestionsOpen(false);
                    }}
                    className="rounded-lg border border-slate-200 bg-white/75 px-3 py-1.5 text-xs font-semibold text-slate-700"
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  disabled={rideStep === 1 ? !ridePickup : !rideDestination}
                  onClick={() => {
                    if (rideStep === 1) {
                      setRideStep(2);
                      setRideMapPickMode("destination");
                    } else {
                      setRideStep(3);
                    }
                    setSearchEditing(false);
                    setSearchQuery("");
                    setSuggestionsOpen(false);
                  }}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {locationError && (
            <p role="alert" className="mt-2 text-xs text-red-600">{locationError}</p>
          )}
          </div>
        </div>
      </div>

      <section aria-label="Choose an ApnaSaathi service" className={`pointer-events-none absolute z-[500] ${(offerRideOpen && homepageOfferStep < 3) || !(driverOpen || offerRideOpen || goodsTransportOpen || passengersOpen || foodCustomersOpen || ridePanelOpen) ? "hidden" : ""} ${(driverOnlineCategory && !driverOpen) || hasActivePassengerTrip || (ridePanelOpen && rideMode === "ride_now") ? "hidden" : ""} ${driverOnlineCategory && driverOpen ? "right-3 top-[min(21rem,46dvh)] w-72 max-w-[calc(100vw-1.5rem)] max-h-[48dvh] overflow-y-auto" : goodsTransportOpen || driverOpen || offerRideOpen || passengersOpen || foodCustomersOpen || ridePanelOpen ? "inset-x-0 bottom-5 px-3 sm:bottom-7 sm:px-6" : "left-3 top-36 bottom-4 sm:left-6 sm:top-40"}`}>
        <div className={`pointer-events-auto rounded-3xl border border-white/60 bg-white/75 p-2.5 shadow-xl backdrop-blur-xl ${goodsTransportOpen || driverOpen || offerRideOpen ? "mx-auto w-full max-w-xl sm:p-3" : ridePanelOpen && rideMode === "ride_now" && rideStep === 3 ? "mx-auto max-w-xl sm:p-3" : passengersOpen || foodCustomersOpen || ridePanelOpen ? "mx-auto max-w-2xl sm:p-5" : "w-56 max-h-full overflow-y-auto sm:w-64"}`}>
          <div className={`${ridePanelOpen || driverOpen || offerRideOpen ? "hidden" : "mb-3 flex items-center justify-between px-1"}`}>
            <div>
            </div>
            {!user && <Link href="/register" className="rounded-full bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700">Sign up</Link>}
          </div>
          <div className={`grid gap-2 ${goodsTransportOpen || driverOpen || offerRideOpen || passengersOpen || foodCustomersOpen || ridePanelOpen ? "grid-cols-2 sm:grid-cols-4 sm:gap-3" : "grid-cols-1"}`}>
            {offerRideOpen ? (
              <div className="col-span-2 min-w-0 sm:col-span-4">
                <HomepageOfferRidePanel
                  sharedMap={sharedMap}
                  pickup={ridePickup}
                  destination={offerDestination}
                  step={homepageOfferStep}
                  onStepChange={setHomepageOfferStep}
                  onClose={() => setOfferRideOpen(false)}
                />
              </div>
            ) : driverOpen ? (
              <HomepageDriverSessionContext.Provider value={driverSessionContextValue}>
                <HomepageDriverPanel map={sharedMap} loggedIn={!!user} activeBooking={hasDriverTrip} compactOnline={!!driverOnlineCategory && !hasDriverTrip} onClose={() => { if (!hasDriverTrip) setDriverOpen(false); }} />
              </HomepageDriverSessionContext.Provider>
            ) : goodsTransportOpen ? (
              <HomepageGoodsTransportPanel map={sharedMap} loggedIn={!!user} onClose={() => setGoodsTransportOpen(false)} />
            ) : passengersOpen ? (
              <HomepagePassengerRadar map={sharedMap} onClose={() => setPassengersOpen(false)} />
            ) : foodCustomersOpen ? (
              <FoodCustomersPanel map={sharedMap} onClose={() => setFoodCustomersOpen(false)} />
            ) : ridePanelOpen ? (
              <div className="col-span-2 max-h-[min(62dvh,560px)] space-y-3 overflow-y-auto pr-1 sm:col-span-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-sm font-bold">
                    {rideMode === "ride_now" ? "Ride Now" : rideMode === "destination" ? "Search Existing Ride" : rideMode === "explore" ? "Nearby Rides" : "Scheduled Request"}
                  </h2>
                  <button
                    type="button"
                    className="text-sm font-semibold text-indigo-700"
                    onClick={() => {
                      ++rideSearchSequenceRef.current;
                      setRidePanelOpen(false);
                      setRideDestination(null);
                      setRideDestinationQuery("");
                      setRideDestinationSuggestions([]);
                      setRideDestinationError("");
                      setRideDestinationLoading(false);
                    }}
                  >
                    Close
                  </button>
                </div>

                <div aria-label="Ride options" className={rideMode === "destination" && rideStep === 1 ? "hidden" : "flex items-start gap-2"}>
                  <button type="button" aria-pressed={rideMode === "ride_now"} onClick={() => openRideMode("ride_now")}
                    className={`min-h-10 rounded-lg border px-3 py-2 text-xs font-semibold ${rideMode === "ride_now" ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-700"}`}>
                    Ride Now
                  </button>
                  <div className="min-w-0 flex-1">
                    <RideModeMenu inline currentMode={rideMode} onSelect={openRideMode} />
                  </div>
                </div>

                <div className={rideMode === "destination" && rideStep !== 1 ? "hidden" : "rounded-xl bg-emerald-50 p-3"}>
                  {!(rideMode === "destination" && rideStep === 1) && (
                    <p className="text-xs font-semibold text-emerald-800">
                      Pickup point
                    </p>
                  )}
                  {rideMode === "destination" && rideStep === 1 && (
                    <div className="mt-2 space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={ridePickupQuery}
                          onChange={(event) => setRidePickupQuery(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              void searchRidePickups();
                            }
                          }}
                          placeholder="Search pickup village, city or place"
                          className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => void searchRidePickups()}
                          className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white"
                        >
                          Search
                        </button>
                        <button
                          type="button"
                          onClick={() => detectLocation()}
                          disabled={gettingLocation}
                          title="Use My Current Location"
                          aria-label="Use My Current Location"
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-indigo-200 bg-white text-indigo-700 disabled:opacity-50"
                        >
                          {gettingLocation
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <MapPin className="h-4 w-4" />}
                        </button>
                      </div>
                      {ridePickupLoading && (
                        <p className="text-xs text-slate-600">Searching locations...</p>
                      )}
                      {ridePickupError && (
                        <p role="alert" className="text-xs text-red-600">
                          {ridePickupError}
                        </p>
                      )}
                      {ridePickupSuggestions.length > 0 && (
                        <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                          {ridePickupSuggestions.map((place, index) => (
                            <button
                              key={`${place.latitude}-${place.longitude}-${index}`}
                              type="button"
                              onClick={() => {
                                void showLocation(
                                  place.latitude,
                                  place.longitude,
                                  place.name
                                );
                                setRidePickupQuery(place.name);
                                setRidePickupSuggestions([]);
                                setRidePickupError("");
                                setRideMapPickMode("pickup");
                                leafletMapRef.current?.flyTo(
                                  [place.latitude, place.longitude],
                                  15
                                );
                              }}
                              className="block w-full border-b border-slate-100 px-3 py-2 text-left text-xs hover:bg-indigo-50"
                            >
                              {place.name}
                            </button>
                          ))}
                        </div>
                      )}

                    </div>
                  )}
                  {!(rideMode === "destination" && rideStep === 1) && (
                    <p className="break-words text-sm">
                      {ridePickup?.label ??
                        "Choose pickup using the top search box, GPS or map."}
                    </p>
                  )}
                  {(rideMode === "ride_now" || rideMode === "destination") && rideStep === 1 && (
                    <button
                      type="button"
                      disabled={!ridePickup}
                      onClick={() => {
                        setRideStep(2);
                        setRideMapPickMode("destination");
                      }}
                      className="mt-3 w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next: Destination →
                    </button>
                  )}
                </div>

                {rideMode !== "explore" && (rideMode === "destination" ? rideStep === 2 : rideMode !== "ride_now" || rideStep === 2) && <>
                  <div className={rideMode === "destination" ? "hidden" : "flex flex-wrap gap-2 text-xs"}>
                    <button type="button" aria-pressed={rideMapPickMode === "pickup"} onClick={() => setRideMapPickMode("pickup")} className={`rounded border px-2 py-1 ${rideMapPickMode === "pickup" ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-slate-200"}`}>Choose pickup on map</button>
                    <button type="button" aria-pressed={rideMapPickMode === "destination"} onClick={() => setRideMapPickMode("destination")} className={`rounded border px-2 py-1 ${rideMapPickMode === "destination" ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-slate-200"}`}>Choose destination on map</button>
                  </div>
                {rideMode !== "destination" && (
                  <label
                    htmlFor="ride-destination"
                    className="block text-xs font-semibold"
                  >
                    Destination
                  </label>
                )}

                <div className="flex gap-2">
                  <input
                    id="ride-destination"
                    value={rideDestinationQuery}
                    onChange={(event) => {
                      ++rideSearchSequenceRef.current;
                      setRideDestinationQuery(event.target.value);
                      setRideDestination(null);
                      setRideDestinationSuggestions([]);
                      setRideDestinationError("");
                      setRideDestinationLoading(false);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void searchRideDestinations();
                      }
                    }}
                    placeholder="Search village, city or destination"
                    className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    disabled={rideDestinationLoading}
                    onClick={() => void searchRideDestinations()}
                    className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {rideDestinationLoading ? "Wait..." : "Search"}
                  </button>
                  {rideMode === "destination" && (
                    <button
                      type="button"
                      title="Use My Current Location"
                      aria-label="Use My Current Location as Destination"
                      disabled={gettingLocation}
                      onClick={() => {
                        if (!navigator.geolocation) {
                          setRideDestinationError("GPS is not supported by this browser.");
                          return;
                        }
                        setGettingLocation(true);
                        setRideDestinationError("");
                        navigator.geolocation.getCurrentPosition(
                          ({ coords }) => {
                            const { latitude, longitude } = coords;
                            const label = `Current location (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`;
                            ++rideSearchSequenceRef.current;
                            setRideDestination({ latitude, longitude, label });
                            setRideDestinationQuery(label);
                            setRideDestinationSuggestions([]);
                            setRideDestinationLoading(false);
                            setRideMapPickMode("destination");
                            leafletMapRef.current?.flyTo([latitude, longitude], 15);
                            setGettingLocation(false);
                          },
                          () => {
                            setRideDestinationError("Could not detect location. Allow GPS permission and try again.");
                            setGettingLocation(false);
                          },
                          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
                        );
                      }}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-indigo-200 bg-white text-indigo-700 disabled:opacity-50"
                    >
                      {gettingLocation
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <MapPin className="h-4 w-4" />}
                    </button>
                  )}
                </div>

                {rideDestinationSuggestions.length > 0 && (
                  <div className="max-h-36 overflow-y-auto rounded-xl border border-slate-200">
                    {rideDestinationSuggestions.map((place, index) => (
                      <button
                        key={`${place.latitude}-${place.longitude}-${index}`}
                        type="button"
                        className="block w-full border-b border-slate-100 px-3 py-2 text-left text-xs hover:bg-indigo-50"
                        onClick={() => {
                          ++rideSearchSequenceRef.current;
                          setRideDestination({ ...place, label: place.name });
                          setRideDestinationQuery(place.name);
                          setRideDestinationSuggestions([]);
                          setRideDestinationError("");
                          setRideDestinationLoading(false);
                        }}
                      >
                        {place.name}
                      </button>
                    ))}
                  </div>
                )}

                {rideDestinationError && (
                  <p role="alert" className="text-xs text-red-600">
                    {rideDestinationError}
                  </p>
                )}

                {rideDestination && rideMode !== "destination" && (
                  <p className="text-xs font-medium text-emerald-700">
                    Destination pin selected on map.
                  </p>
                )}

                  {(rideMode === "ride_now" || rideMode === "destination") && rideStep === 2 && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setRideStep(1);
                          setRideMapPickMode("pickup");
                        }}
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold"
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        disabled={!ridePickup || !rideDestination}
                        onClick={() => setRideStep(3)}
                        className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        {rideMode === "destination" ? "Next: Travel Date →" : "Next: Ride Details →"}
                      </button>
                    </div>
                  )}
                </>}

                {rideMode === "destination" && rideStep === 3 && (
                  <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
                    <div className="rounded-lg bg-white p-3">
                      <p className="text-xs font-semibold text-slate-500">Pickup Point</p>
                      <p className="break-words text-sm font-semibold text-slate-900">
                        {ridePickup?.label ?? "Pickup not selected"}
                      </p>
                    </div>

                    <div className="rounded-lg bg-white p-3">
                      <p className="text-xs font-semibold text-slate-500">Destination Point</p>
                      <p className="break-words text-sm font-semibold text-slate-900">
                        {rideDestination?.label ?? "Destination not selected"}
                      </p>
                    </div>

                    <label htmlFor="existing-ride-date" className="block text-sm font-bold text-indigo-900">
                      Travel Date
                    </label>
                    <input
                      id="existing-ride-date"
                      type="date"
                      value={existingRideDate}
                      min={new Date().toLocaleDateString("en-CA")}
                      onChange={(event) => {
                        setExistingRideDate(event.target.value);
                        setExistingRideResultsOpen(false);
                      }}
                      className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-3 text-sm"
                    />

                    <button
                      type="button"
                      disabled={!ridePickup || !rideDestination || !existingRideDate}
                      onClick={() => setExistingRideResultsOpen(true)}
                      className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
                    >
                      Search Existing Rides
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setExistingRideResultsOpen(false);
                        setRideStep(2);
                        setRideMapPickMode("destination");
                      }}
                      className="w-full rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700"
                    >
                      ← Back to Destination
                    </button>
                  </div>
                )}

                {rideMode === "ride_now" && rideStep === 3 && (
                  <div className="flex items-center justify-between rounded-lg bg-indigo-50 px-3 py-2">
                    <span className="text-sm font-semibold text-indigo-800">
                      Step 3: Ride Details
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setRideStep(2);
                        setRideMapPickMode("destination");
                      }}
                      className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-700"
                    >
                      ← Back to Destination
                    </button>
                  </div>
                )}

                {rideMode === "explore" ? (
                  <NearbyRidesPanel map={sharedMap} pickup={ridePickup} onUseDestination={(point) => {
                    setRideDestination(point); setRideDestinationQuery(point.label); setRideMode("destination"); setRideMapPickMode("destination");
                  }} />
                ) : rideMode === "ride_now" || rideMode === "scheduled" ? (
                  rideMode === "ride_now" ? null : (
                    <div className="space-y-3">
                      <Link
                        href="/my-ride-requests"
                        className="flex w-full items-center justify-between rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-bold text-indigo-700 shadow-sm transition-colors hover:bg-indigo-100"
                      >
                        <span>📋 My Ride Requests</span>
                        <span aria-hidden="true">→</span>
                      </Link>
                      <PassengerRideRequestPanel mode="Scheduled"
                        pickup={ridePickup} destination={rideDestination} map={sharedMap} loggedIn={!!user} />
                    </div>
                  )
                ) : (
                <div className={rideMode === "destination" && !existingRideResultsOpen ? "hidden" : "space-y-1"}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-700">
                      Available rides
                    </span>
                    <span className="text-[10px] text-slate-500">
                      {compactRideRows.length} shown
                    </span>
                  </div>

                  {compactRideLoading && (
                    <p className="py-2 text-xs text-slate-500">
                      Loading rides...
                    </p>
                  )}

                  {compactRideNeedsLogin && (
                    <p className="py-2 text-xs text-slate-600">
                      <Link href="/login" className="font-semibold text-indigo-700 underline">
                        Login
                      </Link>
                      {" "}to see available rides.
                    </p>
                  )}

                  {compactRideError && (
                    <p role="alert" className="py-2 text-xs text-red-600">
                      {compactRideError}
                    </p>
                  )}

                  {!compactRideLoading &&
                    !compactRideNeedsLogin &&
                    !compactRideError &&
                    compactRideRows.length === 0 && (
                      <p className="py-2 text-xs text-slate-500">
                        {!ridePickup || !rideDestination
                          ? "Select pickup and destination to see matching rides."
                          : "No matching rides available right now."}
                      </p>
                    )}

                  <CompactRideList rides={compactRideRows} onSelect={(ride) => {
                    setSelectedCompactRideId(ride.id);
                    if (typeof ride.latitude === "number" && typeof ride.longitude === "number" &&
                        Number.isFinite(ride.latitude) && Number.isFinite(ride.longitude) &&
                        Math.abs(ride.latitude) <= 90 && Math.abs(ride.longitude) <= 180) {
                      leafletMapRef.current?.flyTo([ride.latitude, ride.longitude], 14);
                    }
                  }} />

                  {selectedCompactRide && ridePickup && rideDestination && (
                    <RideBookingConfirmation
                      key={`${selectedCompactRide.id}:${ridePickup.latitude}:${ridePickup.longitude}:${rideDestination.latitude}:${rideDestination.longitude}`}
                      ride={selectedCompactRide}
                      pickup={ridePickup}
                      destination={rideDestination}
                      onClose={() => setSelectedCompactRideId(null)}
                      onBooked={() => {
                        setCompactRideRows((rides) => rides.filter((ride) => ride.id !== selectedCompactRide.id));
                        setSelectedCompactRideId(null);
                      }}
                    />
                  )}
                </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
}
